import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { useId } from 'react';

import { Text, api, isDefined, isTruthy, useLoggedInAuthContext } from 'common';

import { DecisionTreeSelect } from '../../../../../../../../components/DecisionTreeSelect';
import { useFlowDefinitionContext } from '../../../../../context';
import { BlockAutocomplete } from '../../../ConfigFields/Autocomplete';
import { BlockValueTemplateFieldArea } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

const TOOLS_HELPER_TEXT = `The tools that the assistant can use during the call.`;
const METADATA_HELPER_TEXT = `Metadata to be added to the call for usage in tool calls.`;

export function AssistantToolsFormFields(props: {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) {
  const { expanded, onChange } = props;
  const id = useId();
  const { value: tools } = useSelectedNodeField<string[]>('config.tools');
  const { hasPermission } = useLoggedInAuthContext();
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const isDecisionTreeToolSelected = tools?.includes(
    api.AssistantToolDefinition.AssistantToolType.traverseDecisionTree
  );
  const hasViewDecisionTreesPermission =
    hasPermission.ViewDecisionTrees || hasPermission.ModifyDecisionTrees;

  return (
    <Accordion variant="elevation" expanded={expanded} onChange={onChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id={id} aria-controls="tools-content">
        <Text variant="heading5">Tools for assistant</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ gap: 2 }}>
        <Stack spacing={2}>
          <BlockAutocomplete
            fieldName="config.tools"
            label="Tools"
            options={Object.values(api.AssistantToolDefinition.AssistantToolType).filter((tool) => {
              switch (tool) {
                case api.AssistantToolDefinition.AssistantToolType.traverseDecisionTree:
                  return hasViewDecisionTreesPermission;
                case api.AssistantToolDefinition.AssistantToolType.EndCall:
                case api.AssistantToolDefinition.AssistantToolType.TransferCall:
                case api.AssistantToolDefinition.AssistantToolType.NotableTransferCall:
                case api.AssistantToolDefinition.AssistantToolType.AssistantHandoff:
                  // EndCall, TransferCall, NotableTransferCall, and AssistantHandoff are not supported for outbound dropdown
                  return false;
                default:
                  return true;
              }
            })}
            multiple={true}
            helperText={TOOLS_HELPER_TEXT}
          />
          {isTruthy(isDecisionTreeToolSelected) && isDefined(organizationId) && (
            <DecisionTreeSelect
              fieldName="config.decisionTreeId"
              organizationId={organizationId}
              helperText="Select a decision tree to use for call flow logic."
            />
          )}
          <BlockValueTemplateFieldArea
            fieldName="config.metadata"
            label="Metadata"
            helperText={METADATA_HELPER_TEXT}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
